// Indicadores clave: período N contra período N-1, en números que entiende un jefe de obra.
import React, { useEffect, useMemo, useState } from 'react';
import { loadDays, listDays, addDays, dayStartMs, periodKpis, delta, fmtKey, fmtMin, todayKey } from './iaData';
import { T, Seg, inputStyle } from './ui';

const PRESETS = [
  { value: 'dia', label: 'Día', days: 1 },
  { value: 'semana', label: 'Semana', days: 7 },
  { value: 'mes', label: '30 días', days: 30 },
  { value: 'custom', label: 'Elegir' },
];

const ROWS = [
  { key: 'actividad', label: 'Índice de actividad', hint: 'Personas + máquinas en movimiento por foto (las quietas no cuentan)', fmt: (v) => v.toFixed(2), good: 'up', color: T.green },
  { key: 'horasHombre', label: 'Horas-hombre estimadas', hint: 'Personas en cada foto × tiempo hasta la siguiente', fmt: (v) => Math.round(v).toLocaleString('es-AR') + ' h', good: 'up', color: T.green },
  { key: 'casco', label: 'Personas con casco', hint: 'Sobre todas las personas vistas en obra', fmt: (v) => Math.round(v) + '%', good: 'up', color: T.yellow },
  { key: 'pico', label: 'Pico de personas', hint: 'Máximo en una misma foto', fmt: (v) => String(v), good: 'up', color: T.text },
  { key: 'horasMaquina', label: 'Horas de máquina activa', hint: 'Máquinas que se movieron entre fotos', fmt: (v) => v.toFixed(1) + ' h', good: 'up', color: '#ea580c' },
  { key: 'diasActivos', label: 'Días con actividad', fmt: (v, k) => `${v} de ${k.diasConFotos}`, good: 'up', color: T.text },
  { key: 'inicio', label: 'Arranque promedio', fmt: fmtMin, good: null, color: T.text },
  { key: 'fin', label: 'Cierre promedio', fmt: fmtMin, good: null, color: T.text },
];

export default function KeyIndicators({ cam, customerId, cfg, endKey, ready = true, feedId }) {
  const [preset, setPreset] = useState('semana');
  const [custom, setCustom] = useState(null);
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const ranges = useMemo(() => {
    if (preset === 'custom' && custom) return custom;
    const D = (PRESETS.find((p) => p.value === preset) || PRESETS[1]).days;
    const end = endKey > todayKey() ? todayKey() : endKey;
    return { n: [addDays(end, -D + 1), end], n1: [addDays(end, -2 * D + 1), addDays(end, -D)] };
  }, [preset, custom, endKey]);

  useEffect(() => {
    if (!ready) return undefined; // espera a que termine de cargar el gráfico (prioridad)
    let alive = true;
    setLoading(true);
    const inR = (r) => (p) => p.ms >= dayStartMs(r[0]) && p.ms < dayStartMs(addDays(r[1], 1));
    loadDays(cam, customerId, listDays(ranges.n1[0], ranges.n[1]), null, { feedId }).then((photos) => {
      if (!alive) return;
      setRes({ n: periodKpis(photos.filter(inR(ranges.n)), cfg), n1: periodKpis(photos.filter(inR(ranges.n1)), cfg) });
      setLoading(false);
    });
    return () => { alive = false; };
  }, [cam, customerId, ranges, cfg, ready, feedId]);

  const days = listDays(ranges.n[0], ranges.n[1]).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Seg options={PRESETS} value={preset} onChange={(v) => { setPreset(v); if (v === 'custom' && !custom) setCustom(ranges); }} />
      {preset === 'custom' && custom && (
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gap: 6, alignItems: 'center', fontSize: 12, color: T.muted }}>
          <span>Antes</span>
          <input type="date" style={inputStyle} value={custom.n1[0]} onChange={(e) => setCustom({ ...custom, n1: [e.target.value, custom.n1[1]] })} />
          <input type="date" style={inputStyle} value={custom.n1[1]} onChange={(e) => setCustom({ ...custom, n1: [custom.n1[0], e.target.value] })} />
          <span>Ahora</span>
          <input type="date" style={inputStyle} value={custom.n[0]} onChange={(e) => setCustom({ ...custom, n: [e.target.value, custom.n[1]] })} />
          <input type="date" style={inputStyle} value={custom.n[1]} onChange={(e) => setCustom({ ...custom, n: [custom.n[0], e.target.value] })} />
        </div>
      )}
      <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.5 }}>
        <b style={{ color: T.text }}>{fmtKey(ranges.n[0])} → {fmtKey(ranges.n[1])}</b> contra <b style={{ color: T.text }}>{fmtKey(ranges.n1[0])} → {fmtKey(ranges.n1[1])}</b>
        {' '}({days} {days === 1 ? 'día' : 'días'} cada uno{cfg.onlyWork ? `, de ${cfg.workFrom} a ${cfg.workTo} h` : ''})
      </div>
      {loading && !res && <div style={{ color: T.muted, fontSize: 13 }}>Calculando…</div>}
      {res && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, opacity: loading ? 0.5 : 1 }}>
          {ROWS.map((r) => {
            const n = res.n[r.key], n1 = res.n1[r.key];
            const d = r.good ? delta(n, n1) : null;
            const up = d != null && d > 0.5, down = d != null && d < -0.5;
            const col = up ? (r.good === 'up' ? T.green : T.red) : down ? (r.good === 'up' ? T.red : T.green) : T.muted;
            return (
              <div key={r.key} title={r.hint} style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'baseline', gap: 8, padding: '8px 0', borderBottom: `1px solid ${T.line}` }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase', color: T.muted }}>{r.label}</div>
                  <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>antes {n1 == null ? '—' : r.fmt(n1, res.n1)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: r.color, lineHeight: 1.1 }}>{n == null ? '—' : r.fmt(n, res.n)}</div>
                  {r.good && (
                    <div style={{ fontSize: 12, fontWeight: 700, color: col }}>
                      {d == null ? (n1 === 0 && n > 0 ? 'nuevo' : 'sin datos antes') : `${up ? '▲' : down ? '▼' : '='} ${Math.abs(d).toFixed(0)}%`}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Calendario de obra: un cuadrado por día, más verde = más horas-hombre
export function ActivityCalendar({ photos, cfg, fromKey, toKey, onDay }) {
  const k = useMemo(() => periodKpis(photos, cfg), [photos, cfg]);
  const days = listDays(fromKey, toKey);
  const max = Math.max(1, ...[...k.porDia.values()].map((d) => d.hh));
  const first = new Date(fromKey + 'T12:00:00Z').getUTCDay();
  const lead = (first + 6) % 7; // lunes primero
  const cells = [...Array(lead).fill(null), ...days];
  const [hover, setHover] = useState(null);
  const info = hover && k.porDia.get(hover);

  return (
    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <div>
        <div style={{ display: 'flex', gap: 6 }}>
        <div style={{ display: 'grid', gridTemplateRows: 'repeat(7, 26px)', gap: 4, fontSize: 10, color: T.muted }}>
          {['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'].map((d) => <span key={d} style={{ lineHeight: '26px' }}>{d}</span>)}
        </div>
        <div style={{ display: 'grid', gridTemplateRows: 'repeat(7, 26px)', gridAutoFlow: 'column', gridAutoColumns: '26px', gap: 4 }}>
          {cells.map((key, i) => {
            if (!key) return <div key={'x' + i} />;
            const d = k.porDia.get(key);
            const bg = !d ? '#f3f4f6' : d.first == null ? '#e5e7eb' : `rgba(16,185,129,${0.18 + 0.82 * (d.hh / max)})`;
            return (
              <div key={key} onMouseEnter={() => setHover(key)} onMouseLeave={() => setHover(null)} onClick={() => onDay(key)}
                style={{ width: 26, height: 26, borderRadius: 6, background: bg, cursor: 'pointer', outline: hover === key ? `2px solid ${T.text}` : 'none', display: 'grid', placeItems: 'center', fontSize: 10, fontWeight: 600, color: d && d.first != null && d.hh / max > 0.5 ? '#fff' : T.muted }}>{+key.slice(8, 10)}</div>
            );
          })}
        </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10, fontSize: 11, color: T.muted, flexWrap: 'wrap' }}>
          <span style={sw('#f3f4f6')} /> sin fotos <span style={sw('#e5e7eb')} /> sin actividad <span style={sw('rgba(16,185,129,.3)')} /> poca <span style={sw(T.green)} /> mucha
        </div>
      </div>
      <div style={{ minWidth: 220, flex: 1, fontSize: 13, color: T.muted, lineHeight: 1.7 }}>
        {hover ? (
          <>
            <div style={{ color: T.text, fontWeight: 700, fontSize: 15 }}>{fmtKey(hover)}</div>
            {!info ? 'La cámara no mandó fotos ese día.' : info.first == null ? `${info.n} fotos, sin actividad detectada.` : (
              <>
                <div>Arrancó <b style={{ color: T.text }}>{fmtMin(minOf(info.first))}</b> · cerró <b style={{ color: T.text }}>{fmtMin(minOf(info.last))}</b></div>
                <div>Horas-hombre <b style={{ color: T.green }}>{Math.round(info.hh)} h</b> · pico <b style={{ color: T.text }}>{info.peak}</b> personas</div>
                <div>Con casco <b style={{ color: T.yellow }}>{info.persons ? Math.round((100 * info.helmet) / info.persons) + '%' : '—'}</b> · {info.n} fotos</div>
              </>
            )}
            <div style={{ fontSize: 11, marginTop: 4 }}>Clic para ver ese día</div>
          </>
        ) : (
          <>
            <div><b style={{ color: T.text }}>{k.diasActivos}</b> días con actividad de {days.length}</div>
            <div>Arranque promedio <b style={{ color: T.text }}>{fmtMin(k.inicio)}</b> · cierre <b style={{ color: T.text }}>{fmtMin(k.fin)}</b></div>
            <div>Horas-hombre del período <b style={{ color: T.green }}>{Math.round(k.horasHombre).toLocaleString('es-AR')} h</b></div>
            <div style={{ fontSize: 11 }}>Pasá el mouse por un día para ver el detalle.</div>
          </>
        )}
      </div>
    </div>
  );
}
const sw = (c) => ({ display: 'inline-block', width: 11, height: 11, borderRadius: 3, background: c });
const minOf = (ms) => { const d = new Date(ms - 3 * 3600e3); return d.getUTCHours() * 60 + d.getUTCMinutes(); };
